import {
  Component,
  OnInit,
  ViewChild,
  ElementRef,
  Renderer2,
  Inject,
  PLATFORM_ID,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { CartService } from '../../../services/cart.service';
import { ItemCart } from '../../../common/item-cart';
import { NotificationService } from '../../../services/notification.service';
import { ProductService } from '../../../services/product.service';
import { ProductDetailDto, ProductFormat } from '../../../common/product-detail.dto';
import { finalize } from 'rxjs/operators';
import { GalaxyCardComponent } from '@app/components/ui/galaxy-card/galaxy-card.component';
import { GalaxyButtonDirective } from '@app/directives/galaxy-button.directive';
import { GalaxyRippleDirective } from '@app/directives/galaxy-ripple.directive';
import { UiService } from '@app/services/ui.service';

interface ComponentState {
  status: 'loading' | 'success' | 'error';
  product?: ProductDetailDto;
  error?: string;
}

@Component({
  selector: 'app-detail-product',
  imports: [
    FormsModule,
    CommonModule,
    RouterModule,
    GalaxyCardComponent,
    GalaxyButtonDirective,
    GalaxyRippleDirective,
  ],
  templateUrl: './detail-product.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './detail-product.component.css',
})
export class DetailProductComponent implements OnInit {
  state: ComponentState = { status: 'loading' };
  quantity: number = 1;
  isAddingToCart = false;
  selectedFormat: ProductFormat | null = null;

  @ViewChild('productImage', { static: false }) productImage!: ElementRef;

  constructor(
    private productService: ProductService,
    private activatedRoute: ActivatedRoute,
    private cartService: CartService,
    private notification: NotificationService,
    private renderer: Renderer2,
    private uiService: UiService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {}

  ngOnInit(): void {
    this.activatedRoute.params.subscribe(() => {
      this.getProductById();
      if (isPlatformBrowser(this.platformId)) {
        window.scrollTo(0, 0);
      }
    });
  }

  getProductById() {
    this.state = { status: 'loading' };
    const id = Number(this.activatedRoute.snapshot.paramMap.get('id'));

    if (isNaN(id) || id <= 0) {
      this.state = { status: 'error', error: 'ID de producto inválido.' };
      return;
    }

    this.productService
      .getProductById(id)
      .pipe(
        finalize(() => {
          // Opcional: acciones de limpieza que se ejecutan siempre
        }),
      )
      .subscribe({
        next: (product) => {
          this.state = { status: 'success', product: product };
          if (product.formats && product.formats.length > 0) {
            this.selectedFormat = product.formats[0]; // Por defecto selecciona el primer formato (5ml o sugerido)
          }
        },
        error: (err) => {
          const errorMessage =
            err.status === 404
              ? 'Producto no encontrado'
              : 'Ocurrió un error al cargar el producto';
          this.state = { status: 'error', error: errorMessage };
        },
      });
  }

  selectFormat(format: ProductFormat) {
    this.selectedFormat = format;
  }

  addCart() {
    if (
      this.state.status !== 'success' ||
      !this.state.product ||
      this.isAddingToCart
    )
      return;
    const product = this.state.product;

    if (this.quantity < 1 || this.quantity > 10) {
      this.notification.showInfo(
        'La cantidad debe estar entre 1 y 10.',
        'Atención',
      );
      return;
    }

    const formatKey = this.selectedFormat ? this.selectedFormat.formatKey : 'full';
    const formatName = this.selectedFormat ? this.selectedFormat.formatName : 'Botella Completa';

    this.isAddingToCart = true;
    this.cartService.addItem(product.id, formatKey, formatName, this.quantity);
    const formatLabel = this.selectedFormat ? ` (${this.selectedFormat.formatName})` : '';
    this.notification.showSuccess('Producto añadido al carrito', `${product.name}${formatLabel}`);
    this.uiService.openMiniCart();

    setTimeout(() => {
      this.isAddingToCart = false;
    }, 2000);
  }

  onMouseMove(event: MouseEvent): void {
    if (!this.productImage) return;
    const imgElement = this.productImage.nativeElement;
    const container = imgElement.parentElement;
    const { left, top, width, height } = container.getBoundingClientRect();
    const x = ((event.clientX - left) / width) * 100;
    const y = ((event.clientY - top) / height) * 100;
    this.renderer.setStyle(imgElement, 'transform-origin', `${x}% ${y}%`);
    this.renderer.setStyle(imgElement, 'transform', 'scale(2)');
  }

  onMouseLeave(): void {
    if (!this.productImage) return;
    this.renderer.setStyle(
      this.productImage.nativeElement,
      'transform',
      'scale(1)',
    );
  }
}
